import { bool, func, number, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import _get from 'lodash/get';

import Typography from 'ravenjs/lib/Typography';
import Col from 'ravenjs/lib/Col';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import Row from 'ravenjs/lib/Row';

import { LogoutIcon } from 'components/Icons';
import MobileUserMenu from 'components/MobileUserMenu';
import withPublicContent from 'components/PublicContent/PublicContentHOC';
import SupportEmailAddress from 'components/SupportEmailAddress';
import SupportPhoneNumber from 'components/SupportPhoneNumber';
import { DEFAULT_COLORS } from 'constants/colors';
import {
    DEFAULT_USER_NAV_ITEMS,
    USER_NAV_HANDBOOK_USER_GUIDE,
    USER_NAV_STATE_ROUTE_MAP,
    USER_NAV_SUPPORT_INFO,
    USER_NAV_TITLE_MAP,
} from 'constants/navigation';
import { APP_PERMISSIONS } from 'constants/permissions';
import { USER_NAV_ITEMS } from 'constants/proptypes';
import Acl from 'modules/acl';
import { selectors as userSelectors } from 'modules/user';
import {
    isNavigationV2Enabled as isNavV2Enabled,
    isOasisCompany,
    isOasisEnabled,
} from 'utils/company';
import { sanitizeMarkup } from 'utils/sanitize';
import { isPartner, isPartnerAdministrator } from 'utils/user';
import { ADMIN_HANDBOOK_ID, EMPLOYEE_HANDBOOK_ID } from 'constants/api';

import NeedHelpSectionPaychex from '../NeedHelpSectionPaychex';
import { UserMenuStyledV1, UserMenuStyledV2 } from './UserMenuStyled';

const LinkList = styled.ul`
    position: relative;
    list-style: none;
    margin: 0;
    padding: 0;
    line-height: normal;
    ${({ theme }) => theme.media.down('md')`
        margin-left: 0.85rem;
    `};
`;

const LinkItem = styled.li`
    align-items: center;
    display: flex;
    position: relative;
    padding: 0.3125rem 0;

    ${({ theme }) => theme.media.down('md')`
        margin: 0.5rem 0 2.263rem;
        font-size: 1.188rem;
        font-weight: normal;
    `};
    ${({ theme }) => theme.media.up('md')`
        font-size: 1rem;
    `};
`;

const MenuItemDescription = styled.span`
    font-family: Roboto;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    color: ${DEFAULT_COLORS.GREY_27};
    white-space: normal;
    overflow-wrap: break-word;
    word-wrap: break-word;
    vertical-align: middle;
`;

const LinkItemV2 = styled.li`
    display: flex;
    flex-direction: column;
    position: relative;
    padding: 8px 12px;
    border-radius: 4px;
    list-style: none;
    cursor: pointer;
    gap: 4px;

    &:hover {
        background: ${DEFAULT_COLORS.BLUE_HIGHLIGHT};
    }
    &:hover ${MenuItemDescription} {
        color: ${DEFAULT_COLORS.BLACK};
    }

    /* Focus-within shows single ring when child is focused */
    &:focus-within {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 0;
    }
`;

const LinkItemButton = styled(NavLink)`
    color: ${DEFAULT_COLORS.BLACK};
    :hover {
        color: ${DEFAULT_COLORS.LINK};
        text-decoration: none;
    }
`;

const LinkItemButtonV2 = styled(NavLink)`
    display: flex;
    flex-direction: column;
    color: ${DEFAULT_COLORS.BLACK};
    text-decoration: none;
    gap: 4px;

    &:hover {
        color: ${DEFAULT_COLORS.BLACK};
        text-decoration: none;
    }

    /* Hide focus outline - parent LinkItemV2 shows ring via :focus-within */
    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: none;
    }
`;

const MenuItemLabel = styled.span`
    font-weight: 400;
    font-family: Roboto;
    font-size: 16px;
    line-height: 150%;
    color: ${DEFAULT_COLORS.BLACK};
    vertical-align: middle;
`;

const UserActions = styled(Col)`
    display: flex;
    justify-content: flex-end;
    margin-top: 2rem;
`;

const SectionColV2 = styled.div`
    display: flex;
    flex-direction: column;
    width: 307px;
    min-width: 240px;
    flex: 0 1 auto;
`;

const SectionHeaderV2 = styled.div`
    font-family: 'Favorit';
    font-weight: 400;
    font-size: 18px;
    line-height: 120%;
    color: ${DEFAULT_COLORS.GREY_27};
    margin-bottom: 8px;
    padding: 0 12px;
`;

const SectionsRowV2 = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    flex-wrap: nowrap;
`;

const SupportSectionV2 = styled.div`
    margin-top: 1rem;
`;

const SupportContentV2 = styled.div`
    padding: 8px 12px;
`;

const LinkListV2 = styled.ul`
    position: relative;
    list-style: none;
    margin: 0;
    padding: 0;
    line-height: normal;
`;

class UserMenu extends Component {
    showLink = (link = {}) => {
        const { permission, to: pathUrl } = link;
        const { partnerSettings, userRole } = this.props;
        const partnerId = get(partnerSettings, 'partnerId', 0);
        const displayUserGuideMenu = get(partnerSettings, 'displayUserGuideMenu', false);
        const supportInfoSubTitle = get(partnerSettings, 'support.supportInfoSubTitle', null);

        if (link.state && link.state === 'dheaudits') {
            return Acl.check(APP_PERMISSIONS.systemDHEAuditsView);
        }

        if (link.state && link.state === 'userGuide') {
            const LINK_TO_PATH = get(USER_NAV_STATE_ROUTE_MAP, 'userGuide.to', '');

            if (displayUserGuideMenu && Boolean(!supportInfoSubTitle)) {
                link.title = USER_NAV_HANDBOOK_USER_GUIDE.TITLE;

                if (Acl.check(APP_PERMISSIONS.viewMenuHandbookGuide)) {
                    link.to = LINK_TO_PATH.replace('DOCUMENT_ID', EMPLOYEE_HANDBOOK_ID);
                } else {
                    link.to = LINK_TO_PATH.replace('DOCUMENT_ID', ADMIN_HANDBOOK_ID);
                }
                return true;
            } else if (isOasisEnabled() && isOasisCompany(partnerId)) {
                link.title = USER_NAV_HANDBOOK_USER_GUIDE.TITLE;

                if (isPartnerAdministrator(userRole) || isPartner({ role: userRole })) {
                    link.to = LINK_TO_PATH.replace('DOCUMENT_ID', ADMIN_HANDBOOK_ID);
                } else {
                    link.to = LINK_TO_PATH.replace('DOCUMENT_ID', EMPLOYEE_HANDBOOK_ID);
                }
                return true;
            }

            return false;
        }

        return (
            pathUrl &&
            (isEmpty(permission) ||
                (!isEmpty(permission) && Acl.check(APP_PERMISSIONS[permission])))
        );
    };

    supportEmailAddress = () => {
        const { partnerSettings } = this.props;
        return get(partnerSettings, 'support.supportEmail', '');
    };

    supportPhoneNumber = () => {
        const { partnerSettings } = this.props;
        return get(partnerSettings, 'support.hotlinePhone', '');
    };

    supportInfoSubTitle = () => {
        const { partnerSettings } = this.props;
        return get(partnerSettings, 'support.supportInfoSubTitle', '');
    };

    showSupportInformation = () => {
        const { partnerSettings } = this.props;
        const displaySupportInfoInAvatar = _get(
            partnerSettings,
            'support.displaySupportInfoInAvatar',
            false
        );
        return displaySupportInfoInAvatar || Boolean(this.supportInfoSubTitle());
    };

    renderSupportSubtitle = () => {
        const { partnerSettings } = this.props;
        const supportInfoSubTitle = get(partnerSettings, 'support.supportInfoSubTitle', null);
        const displaySupportInfoInAvatar = get(
            partnerSettings,
            'support.displaySupportInfoInAvatar',
            false
        );
        const partnerId = get(partnerSettings, 'partnerId', '');
        const paychexSupportEmail = get(partnerSettings, 'support.supportEmail', '');

        const isPaychexPartner =
            partnerId === MAESTER.companies.paychexinc_id ||
            partnerId === MAESTER.companies.paychexsales_id ||
            partnerId === MAESTER.companies.paychexFlex_id;

        if (isPaychexPartner) {
            return <NeedHelpSectionPaychex paychexSupportEmail={paychexSupportEmail} />;
        } else if (displaySupportInfoInAvatar) {
            return (
                <>
                    <SupportPhoneNumber
                        margin="0.3125rem 0"
                        phoneNumber={this.supportPhoneNumber()}
                    />
                    <SupportEmailAddress
                        emailAddress={this.supportEmailAddress()}
                        margin="0.3125rem 0"
                    />
                </>
            );
        }
        return (
            <Typography color={DEFAULT_COLORS.BLACK} fontFamily="Roboto" lineHeight="normal">
                {supportInfoSubTitle}
            </Typography>
        );
    };

    renderUserLinks = () => {
        const { isMobile, items, onNavClick, userCompany } = this.props;
        const { companyId, partnerId } = userCompany;

        const isNavigationV2Enabled = isNavV2Enabled();

        const onClick = navLink => {
            const { isSoftLogin, openLoginPrompt } = this.props;

            if (isSoftLogin) {
                openLoginPrompt();
            }

            onNavClick(navLink);
        };

        const menuSections = Object.keys(items);

        const getMobileUserNavItems = () => {
            if (get(items, 'myTools.0.state', '') === 'userGuide') {
                this.showLink(items.myTools[0]);
            }

            return items;
        };

        if (isMobile) {
            return (
                <MobileUserMenu
                    userNavItems={getMobileUserNavItems()}
                    onClose={navLink => {
                        onClick(navLink);
                    }}
                />
            );
        }

        if (isNavigationV2Enabled) {
            let menuItemIndex = 0;
            return (
                <>
                    <SectionsRowV2 role="presentation">
                        {menuSections.map(section => {
                            const linkList = items[section].filter(link => this.showLink(link));
                            return (
                                linkList.length > 0 && (
                                    <SectionColV2 key={section} role="presentation">
                                        <SectionHeaderV2
                                            id={`user-menu-section-${section}`}
                                            role="presentation"
                                        >
                                            {USER_NAV_TITLE_MAP[section]}
                                        </SectionHeaderV2>
                                        <LinkListV2 role="presentation">
                                            {linkList.map(link => {
                                                const currentIndex = menuItemIndex;
                                                menuItemIndex += 1;
                                                const pendoClass =
                                                    link.state === 'companies' &&
                                                    Acl.check(
                                                        APP_PERMISSIONS.userAssistanceClientAdmin
                                                    )
                                                        ? 'pendo-nav-userprofile-companies'
                                                        : '';
                                                if (link.state === 'company') {
                                                    link.to = `/admin/companies/${partnerId}/${companyId}/edit?isClientUser=true`;
                                                }

                                                const {
                                                    title,
                                                    description,
                                                    permission,
                                                    state,
                                                    ...linkProps
                                                } = link;

                                                return (
                                                    <LinkItemV2 key={state} role="presentation">
                                                        <LinkItemButtonV2
                                                            {...linkProps}
                                                            onClick={() => {
                                                                onClick(link);
                                                            }}
                                                            className={pendoClass}
                                                            data-avatar-menu-item="true"
                                                            data-testid={`avatar-menu-link-${state}`}
                                                            tabIndex={-1}
                                                            role="menuitem"
                                                            id={`avatar-menu-item-${currentIndex}`}
                                                        >
                                                            <MenuItemLabel>{title}</MenuItemLabel>
                                                            {description && (
                                                                <MenuItemDescription>
                                                                    {description}
                                                                </MenuItemDescription>
                                                            )}
                                                        </LinkItemButtonV2>
                                                    </LinkItemV2>
                                                );
                                            })}
                                        </LinkListV2>
                                    </SectionColV2>
                                )
                            );
                        })}
                    </SectionsRowV2>
                    {this.showSupportInformation() && (
                        <SupportSectionV2 role="presentation">
                            <SectionHeaderV2 role="presentation">
                                {USER_NAV_SUPPORT_INFO.TITLE}
                            </SectionHeaderV2>
                            <SupportContentV2 role="presentation">
                                {this.renderSupportSubtitle()}
                            </SupportContentV2>
                        </SupportSectionV2>
                    )}
                    <Row>
                        <UserActions size={12}>
                            {this.showLink(DEFAULT_USER_NAV_ITEMS) && (
                                <LinkItemV2 key={DEFAULT_USER_NAV_ITEMS.id} role="presentation">
                                    <LinkItemButtonV2
                                        {...DEFAULT_USER_NAV_ITEMS}
                                        onClick={() => onClick({ state: 'logout' })}
                                        data-avatar-menu-item="true"
                                        tabIndex={-1}
                                        role="menuitem"
                                        id={`avatar-menu-item-${menuItemIndex}`}
                                    >
                                        <MenuItemLabel>
                                            {DEFAULT_USER_NAV_ITEMS.title}
                                            {DEFAULT_USER_NAV_ITEMS.id === 'logout' && (
                                                <LogoutIcon
                                                    width="14px"
                                                    height="15px"
                                                    style={{
                                                        marginLeft: '4px',
                                                        verticalAlign: 'middle',
                                                    }}
                                                />
                                            )}
                                        </MenuItemLabel>
                                    </LinkItemButtonV2>
                                </LinkItemV2>
                            )}
                        </UserActions>
                    </Row>
                </>
            );
        }

        return (
            <>
                <Row margin="0 0 0 1rem">
                    {menuSections.map(section => {
                        const linkList = items[section].filter(link => this.showLink(link));
                        return (
                            linkList.length && (
                                <Col style={{ minWidth: '170px' }} key={section}>
                                    <Typography
                                        color={DEFAULT_COLORS.BLACK}
                                        fontSize="1rem"
                                        fontWeight="bold"
                                        fontFamily="Roboto"
                                        lineHeight="normal"
                                        gutterBottom="15px"
                                    >
                                        {USER_NAV_TITLE_MAP[section]}
                                    </Typography>
                                    <LinkList>
                                        {linkList.map(link => {
                                            const pendoClass =
                                                link.state === 'companies' &&
                                                Acl.check(APP_PERMISSIONS.userAssistanceClientAdmin)
                                                    ? 'pendo-nav-userprofile-companies'
                                                    : '';
                                            if (link.state === 'company') {
                                                link.to = `/admin/companies/${partnerId}/${companyId}/edit?isClientUser=true`;
                                            }

                                            return (
                                                <LinkItem key={link.state}>
                                                    <LinkItemButton
                                                        {...link}
                                                        onClick={() => {
                                                            onClick(link);
                                                        }}
                                                        data-testid={`avatar-menu-link-${link.state}`}
                                                        dangerouslySetInnerHTML={sanitizeMarkup(
                                                            link.title
                                                        )}
                                                        className={pendoClass}
                                                    />
                                                </LinkItem>
                                            );
                                        })}
                                    </LinkList>
                                </Col>
                            )
                        );
                    })}
                </Row>
                {this.showSupportInformation() && (
                    <Row margin="1rem 1rem 0">
                        <Col>
                            <Typography
                                color={DEFAULT_COLORS.BLACK}
                                fontSize="1rem"
                                fontWeight="bold"
                                fontFamily="Roboto"
                                lineHeight="normal"
                                gutterBottom="0.9375rem"
                            >
                                {USER_NAV_SUPPORT_INFO.TITLE}
                            </Typography>
                            {this.renderSupportSubtitle()}
                        </Col>
                    </Row>
                )}
                <Row>
                    <UserActions size={12}>
                        {this.showLink(DEFAULT_USER_NAV_ITEMS) && (
                            <LinkItem key={DEFAULT_USER_NAV_ITEMS.id}>
                                {DEFAULT_USER_NAV_ITEMS.id === 'logout' ? (
                                    <LogoutIcon
                                        width="14px"
                                        height="15px"
                                        style={{ marginRight: '15px' }}
                                    />
                                ) : null}
                                <LinkItemButton
                                    {...DEFAULT_USER_NAV_ITEMS}
                                    onClick={() => onClick({ state: 'logout' })}
                                    dangerouslySetInnerHTML={sanitizeMarkup(
                                        DEFAULT_USER_NAV_ITEMS.title
                                    )}
                                />
                            </LinkItem>
                        )}
                    </UserActions>
                </Row>
            </>
        );
    };

    render() {
        const { isMobile } = this.props;

        const isNavigationV2Enabled = isNavV2Enabled();

        const UserMenuContainer =
            !isMobile && isNavigationV2Enabled ? UserMenuStyledV2 : UserMenuStyledV1;

        return (
            <UserMenuContainer role={isNavigationV2Enabled ? 'presentation' : undefined}>
                {this.renderUserLinks()}
            </UserMenuContainer>
        );
    }
}

UserMenu.propTypes = {
    history: object,
    isMobile: bool,
    isSoftLogin: bool.isRequired,
    items: USER_NAV_ITEMS,
    onNavClick: func,
    openLoginPrompt: func.isRequired,
    partnerSettings: shape({
        partnerId: number,
    }).isRequired,
    userCompany: shape({
        companyId: number,
    }).isRequired,
    userRole: string.isRequired,
};

UserMenu.defaultProps = {
    history: {},
    isMobile: false,
    items: {},
    onNavClick: () => {},
};

const mapStateToProps = createStructuredSelector({
    partnerSettings: userSelectors.getPartnerSettings,
    userCompany: userSelectors.getUserCompany,
    userRole: userSelectors.getUserRole,
});

export { UserMenu as UserMenuUnwrapped };
export default withPublicContent(connect(mapStateToProps, null)(UserMenu));
